// useActiveSession — owns the in-flight session state cluster.
//
// Pre-extraction, App.jsx had 4 useState declarations for the active +
// completed session, the active story slug, and the pending concept card.
// They were tightly coupled (clearing the active also clears the
// completed; starting a new session clears any pending concept; etc.)
// but the coupling was implicit, repeated inline at 6+ sites.
//
// This hook keeps the same individual setters available (callers that
// only update one slot still can) and adds a few intent-named methods
// that bundle the common transitions:
//
//   clearActive()                  — both activeSession + completedSession
//                                    null. Used on home/forced-placement.
//   startConcept(card, moduleId, session)
//                                  — stash a pending concept-card teach
//                                    step before the session starts.
//   startSessionAfterConcept()     — concept card acknowledged → unstash
//                                    pendingConcept, mount its session.
//   beginSession(session)          — set activeSession directly (skips
//                                    the concept-card step).
//   completeSession(final)         — final accepted, move from active →
//                                    completed in one shot.
//
// The hook is JSX-free and has no side effects; it's pure state plus
// memoized callbacks. Easy to test in isolation if we ever need to.

import { useCallback, useState } from "react";

export default function useActiveSession() {
  const [activeSession, setActiveSession] = useState(null);
  const [completedSession, setCompletedSession] = useState(null);
  const [activeStorySlug, setActiveStorySlug] = useState(null);
  const [pendingConcept, setPendingConcept] = useState(null);

  const clearActive = useCallback(() => {
    setActiveSession(null);
    setCompletedSession(null);
  }, []);

  const startConcept = useCallback((card, moduleId, session) => {
    setPendingConcept({ card, moduleId, session });
  }, []);

  const startSessionAfterConcept = useCallback(() => {
    let stash = null;
    setPendingConcept((cur) => { stash = cur; return null; });
    if (stash?.session) setActiveSession(stash.session);
    return stash;
  }, []);

  const beginSession = useCallback((session) => {
    setPendingConcept(null);
    setActiveSession(session);
  }, []);

  const completeSession = useCallback((final) => {
    setCompletedSession(final);
    setActiveSession(null);
  }, []);

  const cancelConcept = useCallback(() => {
    setPendingConcept(null);
    setActiveSession(null);
  }, []);

  return {
    activeSession,
    completedSession,
    activeStorySlug,
    pendingConcept,
    setActiveSession,
    setCompletedSession,
    setActiveStorySlug,
    setPendingConcept,
    clearActive,
    startConcept,
    startSessionAfterConcept,
    beginSession,
    completeSession,
    cancelConcept,
  };
}
